import { useState, useEffect } from 'react';
import {
  Brain, Trophy, Lightning, ArrowUp, ArrowDown, ChartBar,
  CircleNotch, Eye, TestTube, Target, Info, CheckCircle,
  XCircle, Clock, Envelope, ArrowRight
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearningInsightsPage() {
  const [data, setData] = useState(null);
  const [learningStatus, setLearningStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/email/learning/insights`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/email/learning/status`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([insights, status]) => {
      setData(insights);
      setLearningStatus(status);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const triggerEvaluation = async () => {
    setEvaluating(true);
    try {
      await fetch(`${API}/email/learning/evaluate`, { method: 'POST', credentials: 'include' });
      const [insights, status] = await Promise.all([
        fetch(`${API}/email/learning/insights`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/email/learning/status`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setData(insights);
      setLearningStatus(status);
    } catch { /* ignore */ }
    setEvaluating(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  const ov = data?.overview || {};
  const techniques = data?.technique_performance || [];
  const optHistory = data?.optimization_history || [];
  const abEvals = data?.ab_evaluations || [];
  const abTests = data?.ab_tests || [];

  // Chart data
  const techniqueChartData = techniques.map(t => ({
    name: t.technique.length > 20 ? t.technique.slice(0, 20) + '...' : t.technique,
    full_name: t.technique,
    wins: t.wins,
    losses: t.losses,
    win_rate: t.win_rate,
    uplift: t.avg_uplift,
  }));

  const PIE_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

  return (
    <div className="animate-fade-in" data-testid="learning-insights-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Leerinzichten</h1>
          <p className="text-sm text-zinc-500 mt-1">Hoe het self-learning A/B systeem jouw emails optimaliseert</p>
        </div>
        <button onClick={triggerEvaluation} disabled={evaluating} data-testid="evaluate-now-btn"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
          {evaluating ? <CircleNotch size={14} className="animate-spin" /> : <Lightning size={14} weight="bold" />}
          {evaluating ? 'Evalueren...' : 'Evalueer Nu'}
        </button>
      </div>

      {/* How It Works Explainer */}
      <div className="border-2 border-indigo-100 bg-gradient-to-r from-indigo-50/40 to-white p-5 mb-6" data-testid="how-it-works">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-indigo-600" />
          <h3 className="font-heading text-sm font-semibold text-indigo-900">Hoe werkt het Self-Learning A/B systeem?</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Genereren', desc: 'Bij elke email gebruikt de AI geleerde inzichten om de beste onderwerpregel en inhoud te genereren.', color: 'bg-blue-500' },
            { step: '2', title: 'A/B Splitsen', desc: `${learningStatus?.ab_test_ratio || '20%'} van de ontvangers krijgt een experimentele variant met een andere techniek.`, color: 'bg-purple-500' },
            { step: '3', title: 'Meten', desc: 'Open rates, clicks en replies worden per variant bijgehouden en na 24 uur geëvalueerd.', color: 'bg-amber-500' },
            { step: '4', title: 'Leren', desc: 'Winnende technieken worden opgeslagen. Verliezende technieken worden vermeden. Het systeem wordt slimmer.', color: 'bg-emerald-500' },
          ].map((s, i) => (
            <div key={i} className="flex gap-3">
              <div className={`w-7 h-7 ${s.color} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>{s.step}</div>
              <div>
                <div className="text-sm font-semibold text-zinc-800">{s.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{s.desc}</div>
              </div>
              {i < 3 && <ArrowRight size={16} className="text-zinc-300 hidden md:block self-center ml-auto flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* KPI Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="learning-kpis">
        {[
          { l: 'Emails Verzonden', v: ov.total_emails_sent || 0, c: 'text-zinc-700' },
          { l: 'A/B Getest', v: ov.ab_tested_emails || 0, c: 'text-indigo-600' },
          { l: 'Controle', v: ov.control_variant_sent || 0, c: 'text-blue-600' },
          { l: 'Test Variant', v: ov.test_variant_sent || 0, c: 'text-purple-600' },
          { l: 'Technieken', v: ov.total_techniques_learned || 0, c: 'text-cyan-600' },
          { l: 'Winnaars', v: ov.winning_techniques || 0, c: 'text-emerald-600' },
          { l: 'Evaluaties', v: ov.total_ab_evaluations || 0, c: 'text-amber-600' },
          { l: 'Optimalisaties', v: ov.total_manual_optimizations || 0, c: 'text-zinc-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white p-3 text-center">
            <div className={`font-data text-lg font-bold ${s.c}`}>{s.v}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="techniques" className="w-full">
        <TabsList className="bg-zinc-100 border border-zinc-200 mb-4 gap-0 flex-wrap">
          {['techniques', 'ab-tests', 'optimizations', 'evaluations'].map(tab => (
            <TabsTrigger key={tab} value={tab} className="text-xs px-4 data-[state=active]:bg-white data-[state=active]:text-[#0F172A] data-[state=active]:font-semibold">
              {tab === 'techniques' ? 'Technieken' : tab === 'ab-tests' ? 'A/B Tests' : tab === 'optimizations' ? 'Optimalisaties' : 'Evaluaties'}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* TECHNIQUES TAB */}
        <TabsContent value="techniques" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Technique Performance Chart */}
            {techniqueChartData.length > 0 && (
              <div className="border border-zinc-200 bg-white p-5" data-testid="technique-chart">
                <h4 className="text-sm font-semibold mb-3">Techniek Prestaties (Wins vs Verliezen)</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={techniqueChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                    <Tooltip content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className="bg-white border border-zinc-200 p-3 shadow-lg text-xs">
                          <div className="font-medium mb-1">{d?.full_name}</div>
                          <div className="text-emerald-600">Wins: {d?.wins}</div>
                          <div className="text-red-400">Verliezen: {d?.losses}</div>
                          <div className="text-indigo-600">Win Rate: {d?.win_rate}%</div>
                          <div className="text-amber-600">Gem. Uplift: +{d?.uplift}%</div>
                        </div>
                      );
                    }} />
                    <Bar dataKey="wins" fill="#10B981" name="Wins" radius={[0, 2, 2, 0]} />
                    <Bar dataKey="losses" fill="#FCA5A5" name="Verliezen" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Win Rate Pie Chart */}
            {techniques.length > 0 && (
              <div className="border border-zinc-200 bg-white p-5" data-testid="winrate-chart">
                <h4 className="text-sm font-semibold mb-3">Win Rate per Techniek</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={techniqueChartData.filter(t => t.wins > 0)} dataKey="wins" nameKey="name"
                      cx="50%" cy="50%" outerRadius={80} label={({ name, win_rate }) => `${name} (${win_rate}%)`}>
                      {techniqueChartData.filter(t => t.wins > 0).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Technique Detail Cards */}
          <div className="mt-5 space-y-3" data-testid="technique-cards">
            <h4 className="text-sm font-semibold">Alle Geleerde Technieken</h4>
            {techniques.length === 0 && (
              <p className="text-sm text-zinc-400 italic py-4">Nog geen technieken geleerd. Het systeem leert automatisch zodra emails worden verzonden en geëvalueerd.</p>
            )}
            {techniques.map((t, i) => (
              <div key={i} className={`border p-4 ${t.wins > 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-zinc-200 bg-white'}`} data-testid={`technique-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {t.wins > 0 ? <Trophy size={18} className="text-amber-500" /> : <TestTube size={18} className="text-zinc-400" />}
                    <span className="font-medium text-sm">{t.technique}</span>
                    <Badge className={`text-xs px-2 py-0.5 ${t.win_rate >= 70 ? 'bg-emerald-100 text-emerald-700' : t.win_rate >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                      {t.win_rate}% win rate
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span className="text-emerald-600 font-medium">{t.wins}W</span>
                    <span className="text-red-400">{t.losses}V</span>
                    <span>{t.total_tests} tests</span>
                    {t.avg_uplift > 0 && <span className="text-indigo-600 font-medium">+{t.avg_uplift}% uplift</span>}
                  </div>
                </div>
                {t.example_subject && (
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
                    <Envelope size={12} />
                    <span>Voorbeeld: <span className="text-zinc-700 italic">"{t.example_subject}"</span></span>
                  </div>
                )}
                {/* Uitleg */}
                <div className="mt-2 text-xs text-zinc-500 leading-relaxed">
                  {t.win_rate >= 70 ? (
                    <span className="flex items-center gap-1.5"><CheckCircle size={12} className="text-emerald-500" /> Deze techniek presteert consistent goed en wordt actief gebruikt bij het genereren van nieuwe emails.</span>
                  ) : t.win_rate >= 40 ? (
                    <span className="flex items-center gap-1.5"><Clock size={12} className="text-amber-500" /> Deze techniek heeft wisselende resultaten. Meer data nodig voor een definitief oordeel.</span>
                  ) : (
                    <span className="flex items-center gap-1.5"><XCircle size={12} className="text-red-400" /> Deze techniek presteert onder het gemiddelde en wordt door het systeem vermeden.</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* A/B TESTS TAB */}
        <TabsContent value="ab-tests" className="mt-0">
          <div className="space-y-3" data-testid="ab-tests-list">
            <h4 className="text-sm font-semibold">Voltooide A/B Tests ({abTests.length})</h4>
            {abTests.length === 0 && <p className="text-sm text-zinc-400 italic py-4">Nog geen voltooide A/B tests.</p>}
            {abTests.map((test, i) => {
              const winnerVar = test.winner === 'a' ? test.variant_a : test.variant_b;
              const loserVar = test.winner === 'a' ? test.variant_b : test.variant_a;
              const winStats = winnerVar?.stats || {};
              const loseStats = loserVar?.stats || {};
              const winRate = winStats.sent > 0 ? ((winStats.opened || 0) / winStats.sent * 100).toFixed(1) : '0';
              const loseRate = loseStats.sent > 0 ? ((loseStats.opened || 0) / loseStats.sent * 100).toFixed(1) : '0';

              return (
                <div key={test.test_id || i} className="border border-zinc-200 bg-white p-4" data-testid={`ab-test-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TestTube size={16} className="text-indigo-600" />
                      <span className="text-sm font-medium">{test.name || `Test ${test.test_id}`}</span>
                      {test.auto_generated && <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700">Auto</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-emerald-600 font-medium">+{test.uplift_pct}% uplift</span>
                      <span className="text-zinc-400">|</span>
                      <span className="text-zinc-500">{test.confidence_level}% zekerheid</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-3 border text-xs ${test.winner === 'a' ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {test.winner === 'a' && <Trophy size={12} className="text-amber-500" />}
                        <span className="font-medium">{test.variant_a?.label || 'Variant A'}</span>
                      </div>
                      <div className="text-zinc-700 italic mb-1.5">"{test.variant_a?.subject || '-'}"</div>
                      <div className="text-zinc-500">Open: {test.winner === 'a' ? winRate : loseRate}% | Sent: {test.winner === 'a' ? winStats.sent : loseStats.sent}</div>
                    </div>
                    <div className={`p-3 border text-xs ${test.winner === 'b' ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {test.winner === 'b' && <Trophy size={12} className="text-amber-500" />}
                        <span className="font-medium">{test.variant_b?.label || 'Variant B'}</span>
                      </div>
                      <div className="text-zinc-700 italic mb-1.5">"{test.variant_b?.subject || '-'}"</div>
                      <div className="text-zinc-500">Open: {test.winner === 'b' ? winRate : loseRate}% | Sent: {test.winner === 'b' ? winStats.sent : loseStats.sent}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* OPTIMIZATIONS TAB */}
        <TabsContent value="optimizations" className="mt-0">
          <div className="space-y-3" data-testid="optimizations-list">
            <h4 className="text-sm font-semibold">Optimalisatie Historiek ({optHistory.length})</h4>
            {optHistory.length === 0 && <p className="text-sm text-zinc-400 italic py-4">Nog geen optimalisaties uitgevoerd.</p>}
            <div className="border border-zinc-200 bg-white overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3 font-medium">Origineel</th>
                    <th className="text-left p-3 font-medium">Geoptimaliseerd</th>
                    <th className="text-left p-3 font-medium">Techniek</th>
                    <th className="text-center p-3 font-medium">Winnaar</th>
                    <th className="text-center p-3 font-medium">Uplift</th>
                    <th className="text-center p-3 font-medium">Zekerheid</th>
                    <th className="text-right p-3 font-medium">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {optHistory.map((h, i) => (
                    <tr key={h.optimization_id || i} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 max-w-48 truncate text-zinc-600" title={h.original}>"{h.original}"</td>
                      <td className="p-3 max-w-48 truncate text-zinc-800 font-medium" title={h.optimized}>"{h.optimized}"</td>
                      <td className="p-3"><Badge className="text-[10px] bg-indigo-100 text-indigo-700">{h.technique}</Badge></td>
                      <td className="p-3 text-center">
                        {h.winner === 'b' ? (
                          <span className="flex items-center justify-center gap-1 text-emerald-600"><ArrowUp size={12} />Nieuw</span>
                        ) : (
                          <span className="flex items-center justify-center gap-1 text-zinc-500"><ArrowDown size={12} />Origineel</span>
                        )}
                      </td>
                      <td className="p-3 text-center font-medium text-indigo-600">+{h.uplift_pct}%</td>
                      <td className="p-3 text-center text-zinc-500">{h.confidence}%</td>
                      <td className="p-3 text-right text-zinc-400">{h.created_at ? new Date(h.created_at).toLocaleDateString('nl-NL') : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* EVALUATIONS TAB */}
        <TabsContent value="evaluations" className="mt-0">
          <div className="space-y-3" data-testid="evaluations-list">
            <h4 className="text-sm font-semibold">Automatische A/B Evaluaties ({abEvals.length})</h4>
            <p className="text-xs text-zinc-500">Dit zijn de resultaten van automatische A/B tests die door het self-learning systeem zijn uitgevoerd op live verzonden emails.</p>
            {abEvals.length === 0 && (
              <div className="border-2 border-dashed border-zinc-200 p-8 text-center" data-testid="no-evaluations">
                <Brain size={32} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-sm text-zinc-500 mb-1">Nog geen automatische evaluaties</p>
                <p className="text-xs text-zinc-400">Het systeem evalueert automatisch nadat emails zijn verzonden en er voldoende data is (min. 24u, min. 3 ontvangers per variant).</p>
              </div>
            )}
            {abEvals.map((ev, i) => (
              <div key={ev.ab_group_id || i} className="border border-zinc-200 bg-white p-4" data-testid={`evaluation-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Brain size={16} className="text-indigo-600" />
                    <span className="text-sm font-medium">Evaluatie: {ev.ab_group_id}</span>
                  </div>
                  <span className="text-xs text-zinc-400">{ev.evaluated_at ? new Date(ev.evaluated_at).toLocaleString('nl-NL') : ''}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className={`p-3 border text-xs ${ev.winner === 'control' ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      {ev.winner === 'control' && <Trophy size={12} className="text-amber-500" />}
                      <span className="font-medium">Controle</span>
                      <Badge className="text-[10px] bg-blue-100 text-blue-700">{ev.control_technique || 'standaard'}</Badge>
                    </div>
                    {ev.control_subject && <div className="text-zinc-700 italic mb-1">"{ev.control_subject}"</div>}
                    <div className="text-zinc-500">
                      Sent: {ev.control_stats?.sent || 0} | Opens: {ev.control_stats?.opened || 0} | Rate: {ev.control_stats?.sent > 0 ? ((ev.control_stats?.opened || 0) / ev.control_stats.sent * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className={`p-3 border text-xs ${ev.winner === 'test' ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      {ev.winner === 'test' && <Trophy size={12} className="text-amber-500" />}
                      <span className="font-medium">Test Variant</span>
                      <Badge className="text-[10px] bg-purple-100 text-purple-700">{ev.test_technique || 'experimenteel'}</Badge>
                    </div>
                    {ev.test_subject && <div className="text-zinc-700 italic mb-1">"{ev.test_subject}"</div>}
                    <div className="text-zinc-500">
                      Sent: {ev.test_stats?.sent || 0} | Opens: {ev.test_stats?.opened || 0} | Rate: {ev.test_stats?.sent > 0 ? ((ev.test_stats?.opened || 0) / ev.test_stats.sent * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className={`font-medium ${ev.winner === 'test' ? 'text-emerald-600' : 'text-blue-600'}`}>
                    Winnaar: {ev.winner === 'test' ? 'Test Variant' : 'Controle'}
                  </span>
                  <span className="text-indigo-600">Uplift: +{ev.uplift_pct}%</span>
                  <span className="text-zinc-500">
                    Conclusie: {ev.winner === 'test'
                      ? `Techniek "${ev.test_technique}" presteerde beter — opgeslagen als winnend patroon`
                      : `De standaard aanpak was beter dan "${ev.test_technique}" — het systeem vermijdt deze techniek`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
